import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, DeleteOutlined as DeleteIcon } from '@mui/icons-material';
import { INDIGO, SLATE } from '../../constants/Colors';
import { usePageMeta } from '../../contexts/PageMetaContext';
import useApiCall from '../../hooks/useApiCall';
import LoadingSpinner from '../../components/LoadingSpinner';
import BottomActionBar from '../../components/BottomActionBar';
import useToast from '../../hooks/useToast';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.875rem',
    bgcolor: SLATE[50],
    '& fieldset': { borderColor: SLATE[200] },
    '&:hover fieldset': { borderColor: SLATE[300] },
    '&.Mui-focused fieldset': { borderColor: INDIGO[400] },
    '&.Mui-focused': { bgcolor: '#ffffff' },
  },
  '& .MuiInputBase-input': { color: SLATE[800], lineHeight: 1.7 },
};

export default function Instructions() {
  const { id: agentId } = useParams();
  const tokens = useSelector(state => state?.tokens?.value);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const { success, error } = useToast();

  const [fileData, setFileData] = useState({});
  const originalFileDataRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [adding, setAdding] = useState(false);

  const [removingKey, setRemovingKey] = useState(null);
  const [removing, setRemoving] = useState(false);

  const openAddDialogRef = useRef(null);

  const isReadOnly = ['stage', 'production'].includes(process.env.REACT_APP_CURRENT_ENVIRONMENT);

  const endpoint = `/v1/apps/${currentApp?.id}/agents/${agentId}/files/prompt_variables`;

  useEffect(() => {
    setPageMeta(prev => ({
      ...prev,
      actions: isReadOnly ? null : (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
          onClick={() => openAddDialogRef.current?.()}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Add Field
        </Button>
      ),
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => setPageMeta({}), []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentRes, variablesRes] = await Promise.all([
        makeRequest(`/v1/apps/${currentApp?.id}/agents/${agentId}`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest(endpoint, { method: 'GET', authTokens: tokens }),
      ]);

      const agentData = agentRes?.agent || agentRes;
      setPageMeta(prev => ({
        ...prev,
        agentName: agentData?.name,
        description: 'Manage prompt variables for this agent.',
      }));

      const data = variablesRes?.file_data || {};
      setFileData(data);
      originalFileDataRef.current = { ...data };
    } catch {
      // error toasted
    } finally {
      setLoading(false);
    }
  }, [agentId, currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddDialog = useCallback(() => {
    setNewFieldName('');
    setAddDialogOpen(true);
  }, []);
  openAddDialogRef.current = openAddDialog;

  const handleAddField = async () => {
    const key = newFieldName.trim();
    if (!key) return;

    if (key in fileData) {
      setAddDialogOpen(false);
      setNewFieldName('');
      error(`"${key}" already exists.`);
      setTimeout(() => {
        const el = document.getElementById(`field-${key}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      return;
    }

    setAdding(true);
    try {
      await makeRequest(endpoint, {
        method: 'PATCH',
        body: { data: { [key]: '' } },
        authTokens: tokens,
      });
      setFileData(prev => ({ ...prev, [key]: '' }));
      originalFileDataRef.current = { ...originalFileDataRef.current, [key]: '' };
      setAddDialogOpen(false);
      setNewFieldName('');
      setTimeout(() => {
        const el = document.getElementById(`field-${key}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } catch {
      // error toasted
    } finally {
      setAdding(false);
    }
  };

  const handleSave = useCallback(async () => {
    const changed = {};
    Object.entries(fileData).forEach(([k, v]) => {
      if (v !== originalFileDataRef.current[k]) changed[k] = v;
    });
    if (!Object.keys(changed).length) return;
    setSaving(true);
    try {
      await makeRequest(endpoint, {
        method: 'PATCH',
        body: { data: changed },
        authTokens: tokens,
      });
      originalFileDataRef.current = { ...fileData };
      success('Instructions saved.');
    } catch {
      // error toasted
    } finally {
      setSaving(false);
    }
  }, [fileData, endpoint, tokens]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await makeRequest(endpoint, {
        method: 'PATCH',
        body: { delete_keys: [removingKey] },
        authTokens: tokens,
      });
      setFileData(prev => {
        const next = { ...prev };
        delete next[removingKey];
        return next;
      });
      const next = { ...originalFileDataRef.current };
      delete next[removingKey];
      originalFileDataRef.current = next;
      setRemovingKey(null);
    } catch {
      // error toasted
    } finally {
      setRemoving(false);
    }
  };

  const sortedEntries = Object.entries(fileData).sort(([a], [b]) => a.localeCompare(b));

  const isDirty = sortedEntries.some(([k, v]) => v !== originalFileDataRef.current[k]);

  if (loading) return <LoadingSpinner message="Loading instructions…" />;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, pb: 10 }}>
      {sortedEntries.length === 0 ? (
        <Box
          sx={{
            bgcolor: '#ffffff',
            border: `1px solid ${SLATE[200]}`,
            borderRadius: '16px',
            textAlign: 'center',
            py: 10,
          }}
        >
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>📋</Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], mb: '6px' }}>
            No instruction fields yet
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[500], mb: 3 }}>
            Add fields to define how this agent should think and behave.
          </Typography>
          {!isReadOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
              onClick={openAddDialog}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              Add Field
            </Button>
          )}
        </Box>
      ) : (
        sortedEntries.map(([key, value]) => (
          <Box
            key={key}
            id={`field-${key}`}
            sx={{
              bgcolor: '#ffffff',
              border: `1px solid ${SLATE[200]}`,
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: '14px',
                bgcolor: SLATE[50],
                borderBottom: `1px solid ${SLATE[200]}`,
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: SLATE[900] }}>
                {key}
              </Typography>
              {!isReadOnly && (
                <IconButton
                  size="small"
                  onClick={() => setRemovingKey(key)}
                  sx={{ color: SLATE[300], '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } }}
                >
                  <DeleteIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ p: 3 }}>
              {(() => {
                const strVal = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '');
                const isLong = strVal.includes('\n') || strVal.length > 80;
                return (
                  <TextField
                    fullWidth
                    multiline={isLong}
                    minRows={isLong ? 3 : undefined}
                    placeholder={`Enter ${key}…`}
                    value={strVal}
                    onChange={e => setFileData(prev => ({ ...prev, [key]: e.target.value }))}
                    disabled={isReadOnly}
                    sx={INPUT_SX}
                  />
                );
              })()}
            </Box>
          </Box>
        ))
      )}

      {!isReadOnly && sortedEntries.length > 0 && (
        <BottomActionBar
          name="Save Changes"
          handleSave={handleSave}
          saving={saving}
          isDirty={isDirty}
        />
      )}

      {/* Add Field Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); setNewFieldName(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
            Add Field
          </Typography>
          <IconButton size="small" onClick={() => { setAddDialogOpen(false); setNewFieldName(''); }} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Field name (e.g. support_email)"
            value={newFieldName}
            onChange={e => setNewFieldName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddField()}
            sx={INPUT_SX}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, borderTop: `1px solid ${SLATE[100]}` }}>
          <Button
            onClick={() => { setAddDialogOpen(false); setNewFieldName(''); }}
            variant="outlined"
            size="small"
            sx={{
              borderColor: SLATE[300],
              color: SLATE[700],
              textTransform: 'none',
              '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddField}
            disabled={!newFieldName.trim() || adding}
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 500, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
          >
            {adding && <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />}
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!removingKey} onClose={() => setRemovingKey(null)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
            Remove Field
          </Typography>
          <IconButton size="small" onClick={() => setRemovingKey(null)} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
            Remove <strong>{removingKey}</strong> from this agent? The saved value will be lost.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button
              onClick={() => setRemovingKey(null)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: SLATE[300],
                color: SLATE[700],
                textTransform: 'none',
                '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemove}
              disabled={removing}
              variant="contained"
              size="small"
              sx={{
                bgcolor: '#ef4444',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
              }}
            >
              {removing && <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />}
              Remove
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
